import {useContext, useEffect, useState} from 'react';

import {ApplicantProfile} from '../types';

import {LoadingContext} from '../../../../contexts/LoadingContext/LoadingContext';
import {useSecureS3Delete} from '../../../../hooks/useSecureS3Delete';
import {useSecureS3Upload} from '../../../../hooks/useSecureS3Upload';
import {useUserAvatar} from '../../../../hooks/useUserAvatar';
import {ApiResponseError} from '../../../../lib/api/v1/types';

export interface Props {
  profile: ApplicantProfile;
  onUpdateProfile: (update: Partial<ApplicantProfile>) => void;
  onSaveSuccess: () => void;
  onSaveError: (error?: ApiResponseError) => void;
}

export const useProfilePictureUpload = ({profile, onUpdateProfile, onSaveSuccess, onSaveError}: Props) => {
  const {updateAvatarUrl} = useUserAvatar();
  const [uploadProfilePicture, setUploadProfilePicture] = useState<File | null>(null);
  const {setLoadingContext} = useContext(LoadingContext);

  const onSuccessfulProfilePictureDeleteCallback = async () => {
    if (errorDeletingProfilePicture) {
      onSaveError(errorDeletingProfilePicture as ApiResponseError);
      return;
    }

    onSaveSuccess();
  };

  const {makeRequest: deleteProfilePicture, error: errorDeletingProfilePicture} = useSecureS3Delete({
    scope: 'applicants',
    ownerId: profile.applicantId,
    objectType: 'profile_picture',
    onSuccess: onSuccessfulProfilePictureDeleteCallback,
  });

  const onSuccessfulProfilePictureUpload = async (response: Response) => {
    const key = response.url.split('?')[0];
    updateAvatarUrl(key);

    if (profile.profilePictureUrl) {
      const imageName = profile.profilePictureUrl.split('/').pop();
      if (imageName) {
        deleteProfilePicture(imageName);
      }
    }

    if (errorDeletingProfilePicture) {
      onSaveError(errorDeletingProfilePicture as ApiResponseError);
      return response;
    }

    onUpdateProfile({
      profilePictureUrl: key,
    });

    return response;
  };

  const {makeRequest: changeProfilePicture, isLoading: isLoadingProfilePicture} = useSecureS3Upload({
    scope: 'applicants',
    ownerId: profile.applicantId,
    objectType: 'profile_picture',
    file: uploadProfilePicture,
    checksum: null,
    onSuccess: onSuccessfulProfilePictureUpload,
  });

  useEffect(() => {
    if (uploadProfilePicture) {
      changeProfilePicture();
      setLoadingContext(true);
    }
  }, [changeProfilePicture, setLoadingContext, uploadProfilePicture]);

  const saveProfilePicture = async (file: File | null): Promise<string | null> => {
    if (isLoadingProfilePicture) return null;

    if (file === null) {
      updateAvatarUrl('');
      if (profile.profilePictureUrl === null) {
        return null;
      }
      const oldPictureUrl = profile.profilePictureUrl;

      onUpdateProfile({
        profilePictureUrl: null,
      });

      deleteProfilePicture(oldPictureUrl.split('/').pop() || '');
      return null;
    }

    setUploadProfilePicture(file);

    return null;
  };

  return {saveProfilePicture};
};
